import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * New-website promo popup (old site → new toyotahj.com / lexushj.com).
 *
 * Two modes:
 *  - GENERIC (auto by route): shown on Home "/", "/offers", "/maintenance" — a
 *    "discover our new site" card with Toyota + Lexus buttons.
 *  - VEHICLE: pass `vehicle={{ url, brand, name }}` — a "see this vehicle on our
 *    new site" card with a single brand-coloured button pointing at the same
 *    vehicle on the new domain.
 *
 * Self-contained (inline styles + one injected <style> for keyframes/hover) so it
 * never clashes with the legacy CSS. Shows once per session per key.
 */

const TOYOTA_URL = "https://toyotahj.com";
const LEXUS_URL = "https://lexushj.com";
const RED = "#EB0A1E";
const DARK = "#12100E";

function routeKind(pathname = "") {
  const p = pathname.toLowerCase().replace(/^\/(ar|en)(?=\/|$)/, "");
  if (p === "" || p === "/") return "home";
  if (p.startsWith("/offers")) return "offers";
  if (p.startsWith("/maintenance")) return "maintenance";
  return null;
}

export default function NewSitePromo({ location, vehicle }) {
  const { i18n } = useTranslation();
  const isAr = (i18n.language || "ar").startsWith("ar");
  const t = (ar, en) => (isAr ? ar : en);
  const [open, setOpen] = useState(false);

  const isVehicle = !!(vehicle && vehicle.url);
  const kind = isVehicle ? "vehicle" : routeKind(location?.pathname);
  const storageKey = isVehicle
    ? `hj_newsite_vehicle_${(vehicle.url || "").split("/").pop()}`
    : kind
      ? `hj_newsite_${kind}`
      : null;

  useEffect(() => {
    if (!kind || !storageKey) { setOpen(false); return; }
    let seen = false;
    try { seen = sessionStorage.getItem(storageKey) === "1"; } catch (e) { /* private mode */ }
    if (seen) { setOpen(false); return; }
    // Small delay so the page paints first.
    const id = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(id);
  }, [kind, storageKey]);

  const close = () => {
    try { if (storageKey) sessionStorage.setItem(storageKey, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const go = (url) => {
    try { if (storageKey) sessionStorage.setItem(storageKey, "1"); } catch (e) { /* ignore */ }
    window.location.href = url;
  };

  if (!open || !kind) return null;

  const brandIsLexus = isVehicle && /lexus|لكزس/i.test(vehicle.brand || "");
  const vehBtnColor = brandIsLexus ? DARK : RED;

  return (
    <div style={S.overlay} dir={isAr ? "rtl" : "ltr"} onClick={close}>
      <style>{CSS}</style>
      <div style={S.card} className="hjnsp-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" aria-label={t("إغلاق", "Close")} style={S.close} className="hjnsp-close" onClick={close}>×</button>

        <div style={S.badge}>{t("✦ جديد", "✦ New")}</div>

        {isVehicle ? (
          <>
            <h2 style={S.title}>{t("شاهد هذه السيارة في موقعنا الجديد", "See this vehicle on our new website")}</h2>
            <p style={S.sub}>
              {t(
                `تجربة أحدث وأسرع لاستعراض ${vehicle.name || "سيارتك"} على موقع ${brandIsLexus ? "لكزس" : "تويوتا"} الجديد.`,
                `A faster, richer experience for ${vehicle.name || "this vehicle"} on the new ${brandIsLexus ? "Lexus" : "Toyota"} website.`,
              )}
            </p>
            <button type="button" className="hjnsp-btn" style={{ ...S.btn, background: vehBtnColor, color: "#fff", width: "100%" }} onClick={() => go(vehicle.url)}>
              {t("افتح في الموقع الجديد", "Open on the new website")}
              <span style={S.arrow}>{isAr ? "←" : "→"}</span>
            </button>
          </>
        ) : (
          <>
            <h2 style={S.title}>{t("جرّب موقعنا الجديد", "Discover our new website")}</h2>
            <p style={S.sub}>
              {t(
                "تجربة أحدث وأسرع وأجمل لتصفّح سيارات تويوتا ولكزس. اختر علامتك للمتابعة.",
                "A faster, richer experience for Toyota & Lexus. Pick your brand to continue.",
              )}
            </p>
            <div style={S.row}>
              <button type="button" className="hjnsp-btn" style={{ ...S.btn, background: RED, color: "#fff", flex: 1 }} onClick={() => go(TOYOTA_URL)}>
                Toyota
              </button>
              <button type="button" className="hjnsp-btn" style={{ ...S.btn, background: DARK, color: "#fff", flex: 1 }} onClick={() => go(LEXUS_URL)}>
                Lexus
              </button>
            </div>
          </>
        )}

        <button type="button" className="hjnsp-later" style={S.later} onClick={close}>
          {t("المتابعة في الموقع الحالي", "Stay on the current site")}
        </button>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 2147483000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
    animation: "hjnspFade .25s ease",
  },
  card: {
    position: "relative", width: "100%", maxWidth: 440, boxSizing: "border-box",
    background: "#fff", borderRadius: 24, padding: "34px 28px 22px",
    boxShadow: "0 40px 90px -20px rgba(0,0,0,0.55)", textAlign: "center",
    fontFamily: "inherit", animation: "hjnspPop .35s cubic-bezier(.22,1,.36,1)",
  },
  close: {
    position: "absolute", top: 14, insetInlineEnd: 14, width: 34, height: 34,
    borderRadius: "50%", border: "none", background: "#f1f1f1", color: "#555",
    fontSize: 22, lineHeight: "34px", cursor: "pointer",
  },
  badge: {
    display: "inline-block", padding: "5px 14px", borderRadius: 999,
    background: "rgba(235,10,30,.1)", color: RED, fontSize: 12, fontWeight: 800,
    letterSpacing: ".12em", marginBottom: 14,
  },
  title: { margin: "0 0 8px", fontSize: 23, fontWeight: 800, color: "#12100E", lineHeight: 1.3 },
  sub: { margin: "0 0 22px", fontSize: 15, lineHeight: 1.6, color: "#6b6b6b" },
  row: { display: "flex", gap: 12 },
  btn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    height: 54, borderRadius: 16, border: "none", cursor: "pointer",
    fontSize: 16, fontWeight: 800, letterSpacing: ".02em",
    transition: "filter .2s, transform .2s",
  },
  arrow: { fontSize: 18, fontWeight: 900 },
  later: {
    marginTop: 16, background: "none", border: "none", cursor: "pointer",
    color: "#9a9a9a", fontSize: 13, fontWeight: 600,
  },
};

const CSS = `
@keyframes hjnspFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes hjnspPop { from { opacity: 0; transform: translateY(24px) scale(.96) } to { opacity: 1; transform: none } }
.hjnsp-btn:hover { filter: brightness(1.08); transform: translateY(-1px) }
.hjnsp-btn:active { transform: translateY(0) }
.hjnsp-close:hover { background: #e6e6e6; color: #111 }
.hjnsp-later:hover { color: #12100E; text-decoration: underline }
`;
